import { BrowserPushService } from 'src/app/services/browser-push/browser-push.service';
import {
  Component,
  OnInit,
  OnDestroy,
  isDevMode,
  ChangeDetectionStrategy,
  effect,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { LanguageService } from './services/language/language.service';
import {
  AuthService,
  StorageService,
  MessageService,
  SessionService,
  UnsavedChangesService,
} from './services';
import { Modal, initFlowbite } from 'flowbite';
import { ModalConfirmationComponent } from './shared/modals';
import { images } from 'src/images';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { UserSortType } from './models/enums';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { firebaseConfig } from 'src/environments/environment';
import { DEFAULT_SUGGESTION_SORT } from './filters';
import { SuggestionService } from './services/suggestion/suggestion.service';
import { APP } from 'src/app/config/app';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { '(window:beforeunload)': 'onBeforeUnload($event)' },
})
export class AppComponent implements OnInit, OnDestroy {
  public title = 'Backoffice client';
  public initialized = false;

  public routeName = '';
  public images = images;

  private readonly unsavedChangesConfirmation =
    viewChild.required<ModalConfirmationComponent>('unsavedChangesConfirmation');
  readonly unsavedChangesModal = signal<Modal | undefined>(undefined);
  readonly unsavedChangesShowed = signal(false);

  constructor(
    private languageService: LanguageService,
    private storageService: StorageService,
    private messageService: MessageService,
    private browserPushService: BrowserPushService,
    private suggestionService: SuggestionService,
    private authService: AuthService,
    private router: Router,
    private sessionService: SessionService,
    public location: Location,
    public unsavedChangesService: UnsavedChangesService,
  ) {
    effect(() => {
      if (this.unsavedChangesService.request()) {
        untracked(() => this.showUnsavedChangesModal());
      }
    });
    storageService.onRouteNameChange$.subscribe(() => {
      this.getRouteName();
    });
  }

  async ngOnInit() {
    try {
      const browserLocale =
        navigator.language.split('-')[0] || navigator.language;
      const storedLanguage =
        (await this.storageService.getLanguage()) || browserLocale;
      this.languageService.setDefaultLanguage(storedLanguage);
      await this.languageService.setLanguage(storedLanguage);

      await this.restoreSession();
    } finally {
      this.initialized = true;
      this.setDefaultData();
      initFlowbite();
      const app = initializeApp(firebaseConfig);
      getAnalytics(app);
    }
  }

  private async restoreSession() {
    if (!(await this.sessionService.restore())) {
      this.authService.clearStorage();
      this.storageService.clearStorage();
      this.router.navigate(['/'], { replaceUrl: true });
      return;
    }
    await this.getToken();
  }

  async getToken() {
    const token = await this.authService.getToken();
    if (!token) {
      await this.setRouteName(APP.DEFAULT_ROUTE);
      return;
    }
    // signInRedirectGuard sends the root url to the stored route, so only the label is left to sync.
    // the last route holds a url, trimmed to its first segment; the route name holds the whole label key
    if (!this.location.path()) {
      const label = (await this.storageService.getRouteName()) || localStorage.getItem(APP.LOCAL_STORAGE_LAST_ROUTE) || APP.DEFAULT_ROUTE;
      await this.setRouteName(label);
    }
    this.messageService.startPolling();
    this.suggestionService.startPolling();
    void this.browserPushService.enable();
  }

  async setDefaultData() {
    const savedUserSort = await this.storageService.getUserSortType();
    if (!savedUserSort)
      await this.storageService.setUserSortType(UserSortType.CREATION);
    const savedSuggestionSort =
      await this.storageService.getSuggestionsSortType();
    if (!savedSuggestionSort)
      await this.storageService.setSuggestionsSortType(DEFAULT_SUGGESTION_SORT);
  }

  public showNotification() {
    return true;
    // return configureStore.getState().notification.show
  }

  public async setRouteName(routeName: string) {
    this.routeName = routeName;
    await this.storageService.setRouteName(routeName);
    this.getRouteName();
  }

  public async getRouteName() {
    this.routeName = (await this.storageService.getRouteName()) || '';
  }

  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.unsavedChangesService.hasUnsavedChanges()) {
      event.preventDefault();
    }
  }

  showUnsavedChangesModal() {
    if (this.unsavedChangesShowed()) return;
    const modal = new Modal(this.unsavedChangesConfirmation().modalContainer().nativeElement, {
      placement: 'center',
      closable: false,
      onHide: () => {
        this.unsavedChangesShowed.set(false);
        this.unsavedChangesService.answer(false);
      },
      onShow: () => this.unsavedChangesShowed.set(true),
    });
    this.unsavedChangesModal.set(modal);
    modal.show();
  }

  leaveWithUnsavedChanges() {
    this.unsavedChangesService.answer(true);
    this.unsavedChangesModal()?.hide();
  }

  ngOnDestroy() {
    this.messageService.stopPolling();
    this.suggestionService.stopPolling();
  }
}
